import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import MobileCarousel from './ui/MobileCarousel';
import { 
  MessageSquare, 
  BarChart2, 
  Zap, 
  Search, 
  TrendingUp, 
  Users 
} from 'lucide-react';

// Constants to avoid magic numbers
const SCROLL_INTERVAL = 5000;
const CARD_WIDTH = 500;
const GAP = 24;
const MOBILE_CARD_WIDTH = 300;

// Card content moved outside component
const CARD_CONTENT = [
  {
    title: "Know What Customers Need, Instantly",
    description: "Our AI analyzes customer feedback, support tickets, and community discussions to identify the most requested features and pain points.",
    icon: <MessageSquare className="w-10 h-10 text-blue-500" aria-hidden="true" />,
    ariaLabel: "Customer Feedback Analysis"
  },
  {
    title: "Prioritize with Confidence",
    description: "Automatically score and rank feature requests based on customer impact, strategic alignment, and implementation effort.",
    icon: <BarChart2 className="w-10 h-10 text-green-600" aria-hidden="true" />,
    ariaLabel: "Feature Prioritization"
  },
  {
    title: "Uncover Hidden Patterns",
    description: "Discover connections between seemingly unrelated feedback to identify underlying customer needs that might otherwise go unnoticed.",
    icon: <Search className="w-10 h-10 text-purple-600" aria-hidden="true" />,
    ariaLabel: "Pattern Discovery"
  },
  {
    title: "Make Data-Driven Decisions",
    description: "Transform qualitative feedback into quantitative insights that help you make informed product decisions.",
    icon: <Zap className="w-10 h-10 text-amber-500" aria-hidden="true" />,
    ariaLabel: "Data-Driven Decision Making"
  },
  {
    title: "Stay Ahead of Trends",
    description: "Identify emerging customer needs and market trends before they become obvious, giving you a competitive advantage.",
    icon: <TrendingUp className="w-10 h-10 text-red-500" aria-hidden="true" />,
    ariaLabel: "Trend Analysis"
  },
  {
    title: "Align Teams Around Customer Value",
    description: "Create a shared understanding of customer priorities across product, engineering, and leadership teams.",
    icon: <Users className="w-10 h-10 text-indigo-500" aria-hidden="true" />,
    ariaLabel: "Team Alignment"
  }
];

const BenefitsCards: React.FC = () => {
  // State management with performance optimizations
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: true
  });
  
  // Refs for performance
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoScrollInterval = useRef<NodeJS.Timeout | null>(null);
  const isMouseOver = useRef(false);
  const isAutoScrolling = useRef(false);

  // SEO Schema using the constant CARD_CONTENT
  const benefitsSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": CARD_CONTENT.map((card, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": card.title,
      "description": card.description
    }))
  };

  // Auto-scroll management
  const stopAutoScroll = useCallback(() => {
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
      autoScrollInterval.current = null;
    }
    isAutoScrolling.current = false;
  }, []);

  const startAutoScroll = useCallback(() => {
    if (isAutoScrolling.current || isMouseOver.current) return;
    isAutoScrolling.current = true;

    // Start immediately without waiting for the first interval
    if (carouselRef.current && !isMouseOver.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      if (scrollLeft < scrollWidth - clientWidth - 10) {
        carouselRef.current.scrollTo({
          left: scrollLeft + CARD_WIDTH + GAP,
          behavior: 'smooth'
        });
      }
    }

    autoScrollInterval.current = setInterval(() => {
      if (!carouselRef.current || isMouseOver.current) return;
      
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      
      if (scrollLeft >= scrollWidth - clientWidth - 10) {
        carouselRef.current.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
      } else {
        carouselRef.current.scrollTo({
          left: scrollLeft + CARD_WIDTH + GAP,
          behavior: 'smooth'
        });
      }
    }, SCROLL_INTERVAL);
  }, []);

  // Optimized scroll button handler
  const updateScrollButtons = useCallback(() => {
    if (!carouselRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    setScrollState({
      canScrollLeft: scrollLeft > 0,
      canScrollRight: scrollLeft < scrollWidth - clientWidth - 10
    });
  }, []);

  // Optimized scroll handler
  const scrollCarousel = useCallback((direction: 'left' | 'right') => {
    if (!carouselRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    const scrollAmount = CARD_WIDTH + GAP;
    
    const newScroll = direction === 'left' 
      ? Math.max(0, scrollLeft - scrollAmount)
      : Math.min(scrollWidth - clientWidth, scrollLeft + scrollAmount);
    
    // Stop auto-scroll when manually scrolling
    stopAutoScroll();
    
    carouselRef.current.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });
  }, [stopAutoScroll]);

  // Effect for scroll management
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleScroll = () => {
      if (!isMouseOver.current) {
        updateScrollButtons();
      }
    };

    const handleMouseEnter = () => {
      isMouseOver.current = true;
      stopAutoScroll();
    };

    const handleMouseLeave = () => {
      isMouseOver.current = false;
      // Only start auto-scroll if the carousel is in view
      const rect = carousel.getBoundingClientRect();
      if (rect.top >= 0 && rect.bottom <= window.innerHeight) {
        startAutoScroll();
      }
    };

    // Set up intersection observer to handle auto-scroll visibility
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isMouseOver.current) {
            // Add 3 second delay before starting auto-scroll
            setTimeout(() => {
              startAutoScroll();
            }, 3000);
          } else {
            stopAutoScroll();
          }
        });
      },
      { threshold: 0.95 }
    );

    observer.observe(carousel);

    carousel.addEventListener('scroll', handleScroll);
    carousel.addEventListener('mouseenter', handleMouseEnter);
    carousel.addEventListener('mouseleave', handleMouseLeave);
    
    updateScrollButtons();

    // Start auto-scroll immediately if the carousel is in view
    const rect = carousel.getBoundingClientRect();
    if (rect.top >= 0 && rect.bottom <= window.innerHeight) {
      // Add 3 second delay before starting auto-scroll
      setTimeout(() => {
        startAutoScroll();
      }, 3000);
    }
    
    return () => {
      observer.disconnect();
      carousel.removeEventListener('scroll', handleScroll);
      carousel.removeEventListener('mouseenter', handleMouseEnter);
      carousel.removeEventListener('mouseleave', handleMouseLeave);
      stopAutoScroll();
    };
  }, [updateScrollButtons, startAutoScroll, stopAutoScroll]);

  return (
    <>
      <Helmet>
        <title>Product Benefits - AI-Powered Customer Feedback Analysis | Strive</title>
        <meta name="description" content="Discover how Strive's AI-powered platform helps you understand customer needs, prioritize features, and make data-driven product decisions." />
        <meta property="og:title" content="Product Benefits - Transform Customer Feedback into Action" />
        <meta property="og:description" content="Learn how Strive helps product teams make better decisions with AI-powered customer feedback analysis." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(benefitsSchema)}
        </script>
      </Helmet>

      <section 
        className="py-24 bg-white"
        aria-label="Product Benefits"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Understand Your Customers Better
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform raw customer feedback into actionable product insights with our AI-powered analysis tools.
            </p>
          </div>
          
          {/* Mobile View with MobileCarousel */}
          <div className="md:hidden">
            <MobileCarousel 
              itemWidth={`${MOBILE_CARD_WIDTH}px`}
              gap={GAP}
              autoScrollDelay={6000}
            >
              {CARD_CONTENT.map((card, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden h-full"
                  role="article"
                  aria-label={card.ariaLabel}
                >
                  <div className="p-6 flex flex-col h-full">
                    <div className="flex-shrink-0 mb-4 p-3 bg-gray-50 rounded-lg inline-flex">
                      {card.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{card.title}</h3>
                    <p className="text-gray-600 flex-grow">{card.description}</p>
                  </div>
                </div>
              ))}
            </MobileCarousel>
          </div>
          
          {/* Desktop View */}
          <div className="hidden md:block">
            <div className="relative">
              {/* Scroll Controls */}
              <div 
                className="absolute -top-16 right-0 flex space-x-2"
                role="navigation"
                aria-label="Carousel Navigation"
              >
                <button 
                  onClick={() => scrollCarousel('left')}
                  disabled={!scrollState.canScrollLeft}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Scroll left"
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button 
                  onClick={() => scrollCarousel('right')}
                  disabled={!scrollState.canScrollRight}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Scroll right"
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Cards Container */}
              <div 
                ref={carouselRef}
                className="overflow-x-auto hide-scrollbar"
                style={{
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch',
                  scrollBehavior: 'smooth',
                  transition: 'scroll-position 0.3s ease-in-out'
                }}
                role="region"
                aria-label="Benefits carousel"
              >
                <div className="inline-flex space-x-6">
                  {CARD_CONTENT.map((card, index) => (
                    <div 
                      key={index}
                      className="flex-none w-[500px] bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
                      style={{ scrollSnapAlign: 'start' }}
                      role="article"
                      aria-label={card.ariaLabel}
                    >
                      <div className="p-8">
                        <div className="flex-shrink-0 mb-6 p-3 bg-gray-50 rounded-lg inline-flex">
                          {card.icon}
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">{card.title}</h3>
                        <p className="text-lg text-gray-600">{card.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BenefitsCards; 